import React, {ReactElement, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../incase-domain/components/button/Button';
import DropdownMenuCompact from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {IElementTextInput} from '../../../../incase-domain/elements/textInput';
import {TEXT_INPUT_REGEX_MAX, TEXT_INPUT_REGEX_MESSAGE_MAX} from '../../../../incase-domain/validators/elements/constant';
import {regexErrorMessageFieldValidator, regexFieldValidator} from '../../../../incase-domain/validators/elements/inputElement';
import {Option} from '../../../../ui/components/common/Select';
import {getErrorClasses} from '../../../../utils/validation';
import DebouncedTextInput from '../common/debounced/DebouncedTextInput';

type highlightedElementChangeHandler = {
    single: (field: keyof IElementTextInput, value: unknown) => void;
    multiple: (fields: (keyof IElementTextInput)[], values: unknown[]) => void;
};

interface TextInputValidationProps {
    highlightedElement: IElementTextInput;
    highlightedElementChangeHandler: {single: (field: keyof IElementTextInput, value: unknown) => void};
    errors: IValidationErrorObject | null;
}

function TextInputValidation({highlightedElement, errors, highlightedElementChangeHandler}: TextInputValidationProps): ReactElement {
    const {t} = useTranslation();

    const [selectedValidation, setSelectedValidation] = useState<Option>({
        label: t('designSidepanel.validation.none'),
        value: 'none',
    });

    useEffect(() => {
        if (highlightedElement.validationType) {
            setSelectedValidation({label: highlightedElement.validationType, value: highlightedElement.validationType});
        }
    }, [highlightedElement.validationType]);

    const validations = [
        {label: t('designSidepanel.validation.none'), value: 'none'},
        {
            label: t('designSidepanel.validation.email'),
            value: 'email',
            regex: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
            errorMessage: t('designSidepanel.validation.emailError'),
        },
        {
            label: t('designSidepanel.validation.numeric'),
            value: 'numeric',
            regex: '^(0|[1-9][0-9]*)$',
            errorMessage: t('designSidepanel.validation.numericError'),
        },
        {
            label: t('designSidepanel.validation.phone'),
            value: 'phone',
            regex: '^(?:(?!00)[0-9]{7,15}|(?:\\+|00)[0-9]{1,3}[0-9]{7,12})$',
            errorMessage: t('designSidepanel.validation.phoneError'),
        },
        {label: t('designSidepanel.validation.regex'), value: 'regex'},
    ];

    const validationOptions = validations.map((validation) => ({label: validation.label, value: validation.value}));

    const getValidationFromSelectedOption = (value: string): {regex: string; errorMessage: string} => {
        const validation = validations.find((validation) => validation.value === value);
        return {regex: validation?.regex || '', errorMessage: validation?.errorMessage || ''};
    };

    const handleValidationChange = (newValue: Option | null): void => {
        newValue && setSelectedValidation(newValue);
        const validationType = (newValue?.value as string) || '';
        if (validationType === 'regex') {
            (highlightedElementChangeHandler as highlightedElementChangeHandler).multiple(
                ['validationType', 'regex', 'regexErrorMessage'],
                [validationType, '^[a-zA-Z0-9]{3,}$', t('designSidepanel.validation.regexError')]
            );
        } else if (validationType === 'none') {
            (highlightedElementChangeHandler as highlightedElementChangeHandler).multiple(
                ['validationType', 'regex', 'regexErrorMessage'],
                ['', '', '']
            );
        } else {
            const {regex, errorMessage} = getValidationFromSelectedOption(validationType);
            (highlightedElementChangeHandler as highlightedElementChangeHandler).multiple(
                ['validationType', 'regex', 'regexErrorMessage'],
                [validationType, regex, errorMessage]
            );
        }
    };

    return (
        <>
            <div className="u-flex u-v-center u-h-between u-gap-2">
                <DropdownMenuCompact
                    width="full"
                    className="c-sidepanel__dropdown"
                    items={[
                        validationOptions.map((el) => ({
                            label: el.label,
                            onClick: () => handleValidationChange(el),
                        })),
                    ]}
                >
                    <Button
                        className="c-sidepanel__dropdown_button"
                        trailing="solar:alt-arrow-down-linear"
                        variant="soft"
                        size="xs"
                        fullWidth
                    >
                        {selectedValidation.label}
                    </Button>
                </DropdownMenuCompact>
            </div>

            {highlightedElement.validationType === 'regex' && (
                <>
                    <div className={`l-group__item ${getErrorClasses(errors, 'regex')}`}>
                        <DebouncedTextInput
                            className="c-sidepanel__input"
                            value={highlightedElement.regex || ''}
                            onChange={(value) => highlightedElementChangeHandler.single('regex', value)}
                            validator={(val) => {
                                const validation = regexFieldValidator.safeParse(val);
                                return validation.success;
                            }}
                            maxLength={TEXT_INPUT_REGEX_MAX}
                            placeholder={t('designSidepanel.validation.regexExpression')}
                        />
                    </div>

                    <div className={`l-group__item ${getErrorClasses(errors, 'regexErrorMessage')}`}>
                        <DebouncedTextInput
                            className="c-sidepanel__input"
                            value={highlightedElement.regexErrorMessage || ''}
                            onChange={(value) => {
                                highlightedElementChangeHandler.single('regexErrorMessage', value);
                            }}
                            validator={(val) => {
                                const validation = regexErrorMessageFieldValidator.safeParse(val);
                                return validation.success;
                            }}
                            placeholder={t('designSidepanel.validation.regexErrorMessage')}
                            maxLength={TEXT_INPUT_REGEX_MESSAGE_MAX}
                        />
                    </div>
                </>
            )}
        </>
    );
}

export default TextInputValidation;
